import { clearAuthSession } from './authSession';
import { persistSessionNative } from './nativeSessionStore';

// Global 401 guard. Wraps window.fetch so that whenever a request that carried
// our token comes back 401, we first try ONE silent /auth/refresh - so an active
// user is never bounced to the login screen just because their token aged out.
// Only a session that's actually over (refresh itself rejected, or a newer login
// on another device superseded this one) clears the local session and redirects
// to login. Non-API fetches (maps, geocoding) and unauthenticated 401s (e.g. a
// wrong OTP at login) are left untouched.
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
const REFRESH_URL = `${API_BASE}/auth/refresh`;

let refreshing: Promise<boolean> | null = null;
function silentRefresh(origFetch: typeof window.fetch): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) return false;
        const res = await origFetch(REFRESH_URL, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return false;
        const data = await res.json();
        if (!data?.accessToken) return false;
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken || data.accessToken);
        persistSessionNative();
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

export function installSessionGuard(): void {
  if ((window as any).__sessionGuardInstalled) return;
  (window as any).__sessionGuardInstalled = true;

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: any, init?: any): Promise<Response> => {
    const res = await origFetch(input, init);
    try {
      if (res.status === 401) {
        const url = typeof input === 'string' ? input : (input?.url || '');
        const hadToken = !!localStorage.getItem('accessToken');
        const path = window.location.pathname;
        const onLogin = path === '/' || path.includes('/login') || path.includes('/signup');
        // Brief grace after login to avoid any race right after signing in.
        const loginAt = Number(localStorage.getItem('loginAt') || 0);
        const inGrace = loginAt > 0 && Date.now() - loginAt < 8000;
        const alreadyRetried = !!init?.__retriedAfterRefresh;

        if (url.startsWith(API_BASE) && hadToken && !onLogin && !inGrace && url !== REFRESH_URL) {
          // Distinguish "used on another device" from a plain expiry, without
          // consuming the body the original caller still needs.
          let superseded = false;
          try { superseded = (await res.clone().json())?.code === 'SESSION_SUPERSEDED'; } catch {}

          if (!superseded && !alreadyRetried) {
            const refreshed = await silentRefresh(origFetch);
            if (refreshed) {
              const newToken = localStorage.getItem('accessToken');
              const retryInit = { ...(init || {}), __retriedAfterRefresh: true, headers: { ...(init?.headers || {}), Authorization: `Bearer ${newToken}` } };
              return window.fetch(input, retryInit);
            }
          }

          localStorage.setItem('sessionEndedReason', superseded ? 'another-device' : 'expired');
          clearAuthSession();
          window.location.href = '/login';
        }
      }
    } catch {}
    return res;
  };
}
